import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight } from 'lucide-react';

interface AccordionProps {
  title: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
  className?: string;
  count?: number;
  icon?: React.ReactNode;
  indicator?: React.ReactNode;
}

// Controlled accordion with consistent width regardless of collapsed/expanded state
const Accordion: React.FC<AccordionProps> = ({ title, defaultOpen = true, children, className, count, icon, indicator }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [contentHeight, setContentHeight] = useState<number>(0);
  const contentRef = useRef<HTMLDivElement>(null);

  // Update internal state when defaultOpen prop changes
  useEffect(() => {
    setIsOpen(defaultOpen);
  }, [defaultOpen]);

  // Measure content height when content changes or accordion opens
  useEffect(() => {
    if (contentRef.current) {
      const height = contentRef.current.scrollHeight;
      // Add a small buffer to ensure content is never cut off
      setContentHeight(height + 20);
    }
  }, [children, isOpen]);

  // Use ResizeObserver to detect content size changes
  useEffect(() => {
    if (!contentRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (contentRef.current) {
          const height = contentRef.current.scrollHeight;
          // Add a small buffer to ensure content is never cut off
          setContentHeight(height + 20);
        }
      }
    });

    resizeObserver.observe(contentRef.current);

    // Also observe all child elements to catch nested content changes
    const observeChildren = () => {
      const children = contentRef.current?.querySelectorAll('*');
      children?.forEach(child => {
        resizeObserver.observe(child);
      });
    };

    // Initial observation of children
    observeChildren();

    // Re-observe children when content changes
    const mutationObserver = new MutationObserver(() => {
      observeChildren();
    });

    if (contentRef.current) {
      mutationObserver.observe(contentRef.current, {
        childList: true,
        subtree: true
      });
    }

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
    
    // Force height recalculation when opening
    if (!isOpen && contentRef.current) {
      setTimeout(() => {
        if (contentRef.current) {
          const height = contentRef.current.scrollHeight;
          setContentHeight(height + 20);
        }
      }, 50); // Small delay to ensure content is rendered
    }
  };

  return (
    <div 
      className={`group rounded-xl border shadow-sm ${className || ''}`} 
      data-is-open={isOpen}
      style={{ 
        backgroundColor: 'var(--color-surface)', 
        borderColor: 'var(--color-border)',
        width: '100%', // Ensure consistent width
        minWidth: '100%' // Prevent width shrinking
      }}
    >
      <div 
        className="cursor-pointer select-none flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4"
        onClick={toggleOpen}
        role="button"
        tabIndex={0}
        data-testid="accordion-button"
        data-is-open={isOpen}
        aria-expanded={isOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleOpen();
          }
        }}
      >
        <div className="flex items-center gap-2">
          {icon && <div className="flex-shrink-0">{icon}</div>}
          <div className="text-base sm:text-lg font-semibold" style={{ color: 'var(--color-text)' }}>{title}</div>
          {!isOpen && indicator && <div className="flex-shrink-0">{indicator}</div>}
          {!isOpen && !indicator && count !== undefined && count > 0 && (
            <span 
              className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold rounded-full"
              style={{ 
                backgroundColor: 'var(--brand-from)', 
                color: 'white' 
              }}
            >
              {count}
            </span>
          )}
        </div>
        <ChevronRight 
          className={`transition-transform duration-200 ${isOpen ? 'rotate-90' : ''} text-[var(--color-text)]/70`} 
          size={20} 
          aria-hidden 
        />
      </div>
      <div
        ref={contentRef}
        data-testid="accordion-content"
        data-is-open={isOpen}
        style={{
          maxHeight: isOpen ? `${contentHeight}px` : '0',
          overflow: 'hidden',
          transition: 'max-height 0.3s ease-out',
          width: '100%', // Ensure content area maintains width
          position: 'relative' // Ensure content is positioned relative to the accordion
        }}
      >
        <div className="px-4 sm:px-6 pb-4 sm:pb-6" style={{ position: 'relative', width: '100%' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Accordion;

